import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MessageCircle, Smartphone, Copy, Check, Loader2, Timer, ExternalLink } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetchJson } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { trackPageView, trackWhatsAppClick, flushEvents } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

const AUTO_OPEN_SECONDS = 5;
import { WHATSAPP_NUMBER, SITE_NAME, SENDER_KEYWORD } from '../lib/brand';

const FALLBACK_PHONE = WHATSAPP_NUMBER;

function detectMobile() {
  if (typeof navigator === 'undefined') return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' &&
      window.matchMedia != null &&
      window.matchMedia('(pointer: coarse)').matches)
  );
}

function getMobileOS() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  return 'other';
}

const handleOpenWhatsApp = (link: string) => {
  if (link.startsWith('whatsapp://') || link.startsWith('intent://')) {
    window.location.href = link;
  } else {
    const win = window.open(link, '_blank');
    if (!win || win.closed) {
      window.location.href = link;
    }
  }
};

export default function CheckoutConfirm() {
  const { orderNum } = useParams<{ orderNum: string }>();
  const [searchParams] = useSearchParams();
  const { getValue } = useCMS();

  // URL data: rebuild deeplink even if sessionStorage is lost
  const totalRaw = searchParams.get('total') || '0';
  const totalCents = parseInt(totalRaw, 10) || 0;

  const phone = getValue('site.whatsapp.number', FALLBACK_PHONE) || FALLBACK_PHONE;
  const isMobile = useMemo(() => detectMobile(), []);
  const os = useMemo(() => getMobileOS(), []);

  // Message must keep the keyword format so the backend regex can detect it.
  const whatsappMsg = useMemo(() => `${SENDER_KEYWORD}落單 ORDER:${orderNum || ''}`, [orderNum]);
  const encodedMsg = useMemo(() => encodeURIComponent(whatsappMsg), [whatsappMsg]);

  // QR code and desktop fallback must use https://wa.me/ because cameras can't scan custom schemes.
  const waMeLink = useMemo(() => `https://wa.me/${phone}?text=${encodedMsg}`, [phone, encodedMsg]);
  const nativeLink = useMemo(
    () => `whatsapp://send/?phone=${phone}&text=${encodedMsg}`,
    [phone, encodedMsg]
  );

  // Auto-open link: native scheme on mobile, wa.me on desktop.
  const whatsappLink = useMemo(
    () => (isMobile ? nativeLink : waMeLink),
    [isMobile, nativeLink, waMeLink]
  );
  const qrLink = waMeLink;

  // Android intent fallbacks for WhatsApp vs WhatsApp Business.
  const intentWhatsApp = useMemo(
    () => `intent://send/?phone=${phone}&text=${encodedMsg}#Intent;scheme=whatsapp;package=com.whatsapp;end`,
    [phone, encodedMsg]
  );
  const intentWhatsAppBusiness = useMemo(
    () => `intent://send/?phone=${phone}&text=${encodedMsg}#Intent;scheme=whatsapp;package=com.whatsapp.w4b;end`,
    [phone, encodedMsg]
  );

  // UI states
  const [countdown, setCountdown] = useState(AUTO_OPEN_SECONDS);
  const [autoOpenAttempted, setAutoOpenAttempted] = useState(false);
  const [showAppChoice, setShowAppChoice] = useState(false);
  const [appOpened, setAppOpened] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Page view tracking
  useEffect(() => {
    trackPageView('/checkout/confirm');
  }, []);

  useEffect(() => {
    document.title = `WhatsApp 確認落單 | ${SITE_NAME}`;
  }, []);

  // Countdown timer
  useEffect(() => {
    if (confirmed || autoOpenAttempted || countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [confirmed, autoOpenAttempted, countdown]);

  // Auto-open WhatsApp once countdown hits zero
  useEffect(() => {
    if (countdown === 0 && !autoOpenAttempted && whatsappLink) {
      setAutoOpenAttempted(true);
      trackWhatsAppClick(orderNum || '', 'auto_open');
      flushEvents();
      handleOpenWhatsApp(whatsappLink);
    }
  }, [countdown, autoOpenAttempted, whatsappLink, orderNum]);

  // Detect whether the user actually left the page (app opened)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        setAppOpened(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // If auto-open did not leave the page within 3.5s, show manual fallback buttons.
  useEffect(() => {
    if (!autoOpenAttempted || appOpened || showAppChoice || confirmed) return;
    const timer = setTimeout(() => {
      setShowAppChoice(true);
    }, 3500);
    return () => clearTimeout(timer);
  }, [autoOpenAttempted, appOpened, confirmed, showAppChoice]);

  // Poll order confirmation status every 4 seconds.
  useEffect(() => {
    if (!orderNum || confirmed) return;

    const poll = async () => {
      try {
        const res = await apiFetchJson<{ status: string }>(`/public/orders/${orderNum}/status`);
        if (res.success && res.data && res.data.status !== 'pending_whatsapp') {
          setConfirmed(true);
          flushEvents();
          // Refresh to the post-confirmation success page after a short delay.
          setTimeout(() => {
            window.location.href = `/o/${orderNum}?total=${totalCents}`;
          }, 1500);
        }
      } catch {
        // Keep polling on transient errors.
      }
    };

    poll();
    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [orderNum, confirmed, totalCents]);

  const handleManualOpen = useCallback(() => {
    if (!whatsappLink) return;
    trackWhatsAppClick(orderNum || '', 'manual_open');
    flushEvents();
    if (!autoOpenAttempted) {
      setAutoOpenAttempted(true);
      setCountdown(0);
    }
    handleOpenWhatsApp(whatsappLink);
  }, [autoOpenAttempted, orderNum, whatsappLink]);

  const handleCopyMessage = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(whatsappMsg);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = whatsappMsg;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [whatsappMsg]);

  if (!orderNum) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">無效的訂單編號</div>
        <Footer />
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="py-12 md:py-20">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2">訂單已確認</h1>
            <p className="text-stone-600">正在跳轉至付款資訊頁...</p>
            <Loader2 size={24} className="animate-spin mx-auto mt-6 text-stone-400" />
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-10 md:py-16">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">WhatsApp 確認落單</h1>
            <p className="text-stone-600">請發送預填訊息以確認訂單</p>
          </div>

          <div className="bg-stone-50 rounded-2xl p-6 md:p-8 space-y-6">
            {/* Order summary */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-stone-500">訂單編號</span>
                <span className="font-bold">#{orderNum}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-stone-500">總金額</span>
                <span className="font-bold text-x1493-primary text-xl">{formatPrice(totalCents)}</span>
              </div>
            </div>

            {/* Countdown / opening state */}
            <div className="text-center space-y-4">
              {!autoOpenAttempted ? (
                <>
                  <div className="w-16 h-16 bg-[#25D366]/10 text-[#25D366] rounded-full flex items-center justify-center mx-auto">
                    <Timer size={32} />
                  </div>
                  <p className="text-lg font-medium">
                    WhatsApp 將於 <span className="text-[#25D366] font-bold text-2xl">{countdown}</span> 秒後自動打開
                  </p>
                  <button
                    onClick={handleManualOpen}
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-2xl hover:bg-[#128C7E] transition active:scale-95"
                  >
                    <MessageCircle size={22} />
                    立即打開 WhatsApp
                  </button>
                </>
              ) : !showAppChoice ? (
                <>
                  <Loader2 size={40} className="animate-spin mx-auto text-[#25D366]" />
                  <p className="text-stone-600">正在嘗試打開 WhatsApp...</p>
                </>
              ) : null}
            </div>

            {/* Manual fallback buttons */}
            {showAppChoice && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="text-center">
                  <p className="text-amber-700 font-medium mb-3">如果 WhatsApp 沒有自動打開，請按以下按鈕</p>
                </div>

                {os === 'android' ? (
                  <div className="grid gap-3">
                    <a
                      href={intentWhatsApp}
                      onClick={(e) => {
                        e.preventDefault();
                        trackWhatsAppClick(orderNum, 'manual_whatsapp');
                        flushEvents();
                        handleOpenWhatsApp(intentWhatsApp);
                      }}
                      className="w-full bg-[#25D366] text-white font-bold py-3 rounded-2xl hover:bg-[#128C7E] transition flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={20} />
                      開啟 WhatsApp
                    </a>
                    <a
                      href={intentWhatsAppBusiness}
                      onClick={(e) => {
                        e.preventDefault();
                        trackWhatsAppClick(orderNum, 'manual_whatsapp_business');
                        flushEvents();
                        handleOpenWhatsApp(intentWhatsAppBusiness);
                      }}
                      className="w-full bg-[#128C7E] text-white font-bold py-3 rounded-2xl hover:bg-[#075E54] transition flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={20} />
                      開啟 WhatsApp Business
                    </a>
                  </div>
                ) : os === 'ios' ? (
                  <a
                    href={nativeLink}
                    onClick={(e) => {
                      e.preventDefault();
                      trackWhatsAppClick(orderNum, 'manual_ios_whatsapp');
                      flushEvents();
                      handleOpenWhatsApp(nativeLink);
                    }}
                    className="w-full bg-[#25D366] text-white font-bold py-3 rounded-2xl hover:bg-[#128C7E] transition flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={20} />
                    開啟 WhatsApp
                  </a>
                ) : (
                  <a
                    href={waMeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackWhatsAppClick(orderNum, 'manual_wame');
                      flushEvents();
                    }}
                    className="w-full bg-[#25D366] text-white font-bold py-3 rounded-2xl hover:bg-[#128C7E] transition flex items-center justify-center gap-2"
                  >
                    <ExternalLink size={20} />
                    在 WhatsApp Web 開啟
                  </a>
                )}
              </div>
            )}

            {/* QR code fallback */}
            <div className="border-t border-stone-200 pt-6">
              <div className="flex items-center justify-center gap-2 text-stone-500 text-sm mb-4">
                <Smartphone size={16} />
                <span>或在另一部手機掃描 QR Code</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-3 rounded-xl shadow-sm border border-stone-200">
                  <QRCodeSVG
                    value={qrLink}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p className="text-xs text-stone-500">掃描後會自動開啟 WhatsApp</p>
              </div>
            </div>

            {/* Copy message fallback */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">訊息無法打開？按此複製</p>
              <div className="bg-white rounded-lg border border-blue-100 p-3 text-sm text-stone-700 break-words mb-3">
                {whatsappMsg}
              </div>
              <button
                onClick={handleCopyMessage}
                className="w-full py-2.5 rounded-xl font-bold border-2 border-blue-200 text-blue-800 hover:bg-blue-100 transition flex items-center justify-center gap-2"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? '已複製' : '複製 WhatsApp 訊息'}
              </button>
            </div>

            {/* Polling notice */}
            <div className="text-center text-xs text-stone-400 flex items-center justify-center gap-1.5">
              <Loader2 size={12} className="animate-spin" />
              系統正在等待 WhatsApp 確認，請勿關閉此頁
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
